import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../../components/Head/SeoTags";
import { RediscoverMock } from "../../../components/Mockups/RediscoverMock";

const Rediscover = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("featurePages.rediscover.seo.title")}
        description={t("featurePages.rediscover.seo.description")}
        keywords={t("featurePages.rediscover.seo.keywords")}
      />

      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("featurePages.rediscover.seo.title"),
            description: t("featurePages.rediscover.seo.description"),
            url: "https://onbookmarks.com/features/rediscover/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "Weekly resurfacing digest of forgotten bookmarks",
                "Content-free email that never lists your bookmarks",
                "Resurfaces old, unopened, non-favorited bookmarks",
                "One-click unsubscribe and a settings toggle",
                "Free"
              ],
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Free for everyone"
              }
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://onbookmarks.com"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Features",
                  item: "https://onbookmarks.com/features/"
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Rediscover",
                  item: "https://onbookmarks.com/features/rediscover/"
                }
              ]
            }
          }),
        }}
      />

      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    {t("featurePages.rediscover.hero.badge")}
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  {t("featurePages.rediscover.hero.title")}
                </h1>
                <p className="lead">
                  {t("featurePages.rediscover.hero.description")}
                </p>
                <div>
                  <Link
                    to={`${process.env.REACT_APP_APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    {t("featurePages.rediscover.hero.cta")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <RediscoverMock />
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.rediscover.problem.title")}
              </h2>
              <p className="lead text-muted">
                {t("featurePages.rediscover.problem.description")}
              </p>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-bookmark-x fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.rediscover.problem.card1.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.rediscover.problem.card1.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-eye-slash fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.rediscover.problem.card2.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.rediscover.problem.card2.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-hourglass-bottom fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.rediscover.problem.card3.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.rediscover.problem.card3.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="mb-3">
                <span className="badge bg-purple text-white px-3 py-2">
                  {t("featurePages.rediscover.solution.badge")}
                </span>
              </div>
              <RediscoverMock />
            </div>
            <div className="col-lg-6">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.rediscover.solution.title")}
              </h2>
              <p className="lead mb-4">
                {t("featurePages.rediscover.solution.description")}
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.rediscover.solution.benefit1.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.rediscover.solution.benefit1.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.rediscover.solution.benefit2.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.rediscover.solution.benefit2.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.rediscover.solution.benefit3.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.rediscover.solution.benefit3.description")}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.rediscover.howItWorks.title")}
            </h2>
            <p className="lead text-muted">
              {t("featurePages.rediscover.howItWorks.description")}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">1</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.rediscover.howItWorks.step1.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.rediscover.howItWorks.step1.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">2</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.rediscover.howItWorks.step2.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.rediscover.howItWorks.step2.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">3</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.rediscover.howItWorks.step3.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.rediscover.howItWorks.step3.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center text-white">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.rediscover.cta.title")}
            </h2>
            <p className="lead mb-4">
              {t("featurePages.rediscover.cta.description")}
            </p>
            <Link
              to={`${process.env.REACT_APP_APP_URL}/register`}
              className="btn btn-light btn-lg px-5"
            >
              {t("featurePages.rediscover.cta.button")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Rediscover;
